import React, { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

/**
 * Shared column sorting for the list tables (rentals, invoices, payments).
 *
 * Lives in one place so the three tables can't drift into three different
 * sort behaviours. Sorting is CLIENT-side over the already-filtered rows —
 * the same place the filters and the pager already operate, so what you
 * sort is exactly what you filtered, and paging follows the sort.
 *
 * Comparator rules (money and dates are the point of these tables):
 *   - numbers compare numerically, never as strings ("9" before "10")
 *   - ISO timestamps/dates compare lexicographically, which is chronological
 *   - everything else compares case-insensitively
 *   - blanks ALWAYS sink to the bottom, in both directions, so "no paid_to"
 *     or "never updated" never masquerades as the oldest/newest row
 */

const isBlank = (v) => v === null || v === undefined || v === '';

export const compareValues = (a, b) => {
  if (isBlank(a) && isBlank(b)) return 0;
  if (isBlank(a)) return 1;   // blanks last regardless of direction
  if (isBlank(b)) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb) && String(a).trim() !== ''
      && String(b).trim() !== '') {
    return na - nb;
  }
  // numeric:true = natural order, so unit "2" < "10" and "P3" < "P10".
  // Harmless for ISO dates (zero-padded, so natural == lexicographic).
  return String(a).localeCompare(String(b), undefined,
    { numeric: true, sensitivity: 'base' });
};

/**
 * Sort STATE only — it takes no rows, so it can always be called at the top
 * of a component, above any early `return` (rules-of-hooks). Apply it to
 * whatever list you have, wherever you have it:
 *
 *     const { applySort, sortKey, sortDir, toggle } = useTableSort(ACCESSORS);
 *     ...
 *     const rows = applySort(filtered);
 *
 * @param accessors map of sortKey -> (row) => comparable value
 * @param initial   {key, dir} starting sort ('asc' | 'desc')
 */
export const useTableSort = (accessors, initial = {}) => {
  const [sortKey, setSortKey] = useState(initial.key || null);
  const [sortDir, setSortDir] = useState(initial.dir || 'desc');

  const applySort = useMemo(() => (rows) => {
    if (!sortKey || !accessors[sortKey] || !Array.isArray(rows)) return rows;
    const get = accessors[sortKey];
    const factor = sortDir === 'asc' ? 1 : -1;
    // Blanks stay last in BOTH directions: compare them before applying
    // the direction factor.
    return [...rows].sort((r1, r2) => {
      const v1 = get(r1);
      const v2 = get(r2);
      if (isBlank(v1) && isBlank(v2)) return 0;
      if (isBlank(v1)) return 1;
      if (isBlank(v2)) return -1;
      return compareValues(v1, v2) * factor;
    });
  }, [accessors, sortKey, sortDir]);

  const toggle = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Dates/amounts are almost always wanted newest/largest first.
      setSortDir('desc');
    }
  };

  return { applySort, sortKey, sortDir, toggle };
};

/** A <th> that sorts. `align` mirrors the cell alignment below it. */
export const SortTh = ({
  label, sortKey: key, current, dir, onSort, align = 'left', className = '',
  dense = false,
}) => {
  const active = current === key;
  const Icon = !active ? ChevronsUpDown : dir === 'asc' ? ChevronUp : ChevronDown;
  return (
    <th
      className={`${dense ? 'px-3' : 'px-4'} py-2.5 text-[11px] font-semibold uppercase tracking-wider select-none whitespace-nowrap ${
        align === 'right' ? 'text-right' : 'text-left'} ${className}`}
    >
      <button
        type="button"
        onClick={() => onSort(key)}
        title={`Sort by ${label}`}
        className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors ${
          align === 'right' ? 'flex-row-reverse' : ''} ${
          active ? 'text-slate-700' : 'text-slate-400 hover:text-slate-600'}`}
      >
        {label}
        <Icon className={`h-3 w-3 ${active ? 'opacity-100' : 'opacity-40'}`} />
      </button>
    </th>
  );
};

export default useTableSort;
